import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { getStoredUser, clearToken, getJuryUnreadCount, sendHeartbeat, endSession } from './api';
import { ToastProvider } from './components/Toast';
import { NotificationBell } from './components/NotificationCenter';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import PolitiqueConfidentialitePage from './pages/PolitiqueConfidentialitePage';
import ConditionsUtilisationPage from './pages/ConditionsUtilisationPage';
import ReglementPage from './pages/ReglementPage';
import FaqPage from './pages/FaqPage';
import SearchPage from './pages/SearchPage';
import StartupDetail from './pages/StartupDetail';
import DashboardPage from './pages/DashboardPage';
import MatrixPage from './pages/MatrixPage';
import ComparePage from './pages/ComparePage';
// Startup portal
import StartupDashboard from './pages/startup/StartupDashboard';
import StartupProfile from './pages/startup/StartupProfile';
import QitPage from './pages/startup/QitPage';
import DataRoomPage from './pages/startup/DataRoomPage';
import StartupEvents from './pages/startup/StartupEvents';
// Investor portal
import WatchlistPage from './pages/investor/WatchlistPage';
import InvestorAlerts from './pages/investor/InvestorAlerts';
import PortfolioPage from './pages/investor/PortfolioPage';
import InvestorProfile from './pages/investor/InvestorProfile';
// Jury
import JurySpace from './pages/jury/JurySpace';
import JuryDashboard from './pages/jury/JuryDashboard';
import JuryMembers from './pages/jury/JuryMembers';
import JuryMessages from './pages/jury/JuryMessages';
// Analyst
import AnalystDossiers from './pages/analyst/AnalystDossiers';
import DossierDetail from './pages/analyst/DossierDetail';
// Admin
import AdminUsers from './pages/admin/AdminUsers';
import AdminWeights from './pages/admin/AdminWeights';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCampaigns from './pages/admin/AdminCampaigns';
import AdminScraping from './pages/admin/AdminScraping';
import AdminTools from './pages/admin/AdminTools';
import AdminJuryMembers from './pages/admin/AdminJuryMembers';

// Auth Context
export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  // endSession lit le jeton avant clearToken : la session est close côté serveur.
  const logout = () => { endSession(); clearToken(); setUser(null); };

  // Signal de présence (Outils > Utilisateurs connectés) : un appel par minute
  // tant que l'onglet est visible, et dès qu'il le redevient.
  useEffect(() => {
    if (!user?.id) return undefined;
    const beat = () => { if (document.visibilityState === 'visible') sendHeartbeat(); };
    beat();
    const timer = setInterval(beat, 60000);
    document.addEventListener('visibilitychange', beat);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', beat); };
  }, [user?.id]);

  return <AuthContext.Provider value={{ user, setUser, logout }}>{children}</AuthContext.Provider>;
}

// Theme Context — light/dark mode with localStorage persistence
export const ThemeContext = createContext(null);
export function useTheme() { return useContext(ThemeContext); }

function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem('isi_theme') || 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('isi_theme', theme);
  }, [theme]);
  const toggleTheme = useCallback(() => {
    setThemeState(t => (t === 'dark' ? 'light' : 'dark'));
  }, []);
  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

function ThemeToggle({ className = '' }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      onClick={toggleTheme}
      className={`theme-toggle ${className}`}
      aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
      title={isDark ? 'Mode clair' : 'Mode sombre'}
    >
      <span aria-hidden="true" style={{ fontSize: 16 }}>
        {isDark ? '☀️' : '🌙'}
      </span>
    </button>
  );
}
export { ThemeToggle };

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function NavbarToggle() {
  const toggle = () => document.body.classList.toggle('sidebar-open');
  return (
    <button onClick={toggle} className="navbar-toggle" aria-label="Ouvrir le menu" type="button">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="white" aria-hidden="true">
        <rect y="3" width="20" height="2" rx="1" />
        <rect y="9" width="20" height="2" rx="1" />
        <rect y="15" width="20" height="2" rx="1" />
      </svg>
    </button>
  );
}

// Lien « Messagerie » du jury, avec la pastille des messages non lus.
// Interrogé toutes les minutes, et immédiatement quand la page messagerie
// signale qu'une conversation vient d'être lue.
function JuryMessagesLink({ className }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = () => getJuryUnreadCount()
      .then(r => { if (alive) setUnread(r.unread || 0); })
      .catch(() => {});
    refresh();
    const t = setInterval(refresh, 60000);
    window.addEventListener('jury-messages-read', refresh);
    return () => { alive = false; clearInterval(t); window.removeEventListener('jury-messages-read', refresh); };
  }, []);
  return (
    <Link to="/jury/messages" className={className}>
      Messagerie
      {unread > 0 && (
        <span style={{ marginLeft: 8, background: '#E74C3C', color: 'white', borderRadius: 10,
          fontSize: 11, fontWeight: 700, padding: '1px 7px' }}>{unread}</span>
      )}
    </Link>
  );
}

// Version affichée en bas du menu. Injectée AU BUILD depuis le tag Git
// (APP_VERSION dans frontend/Dockerfile, alimenté par la CI avec le tag qui
// déclenche la construction) : l'affichage suit donc exactement la version
// déployée, sans retouche manuelle à chaque livraison. Hors build taggé — une
// image construite à la main en local — on ne prétend pas à un numéro.
const RAW_VERSION = process.env.REACT_APP_VERSION || '';
const VERSION_LABEL = /^v?\d+\.\d+/i.test(RAW_VERSION)
  ? 'V' + RAW_VERSION.replace(/^v/i, '')
  : 'version de développement';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isActive = (path) => location.pathname.startsWith(path) ? 'active' : '';
  const handleLogout = () => { logout(); navigate('/'); };
  // Close mobile drawer when navigating
  useEffect(() => { document.body.classList.remove('sidebar-open'); }, [location.pathname]);

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-brand">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect width="32" height="32" rx="8" fill="#4A71E5"/>
          <text x="6" y="23" fontSize="18" fontWeight="bold" fill="white">ISI</text>
        </svg>
        Plateforme <span>ISI</span>
      </Link>
      <div className="navbar-links">
        {!user && (
          <>
            <Link
              to="/login"
              className={isActive('/login')}
              onMouseDown={(e) => e.preventDefault()}
            >
              Se connecter
            </Link>
            <Link
              to="/register"
              className={isActive('/register')}
              onMouseDown={(e) => e.preventDefault()}
            >
              Créer un compte
            </Link>
          </>
        )}
        {user?.role === 'startup' && (
          <>
            <Link to="/startup/dashboard" className={isActive('/startup/dashboard')}>Mon dossier</Link>
            <Link to="/startup/profile" className={isActive('/startup/profile')}>Profil</Link>
            <Link to="/startup/qit" className={isActive('/startup/qit')}>Questionnaire ISI</Link>
            <Link to="/startup/dataroom" className={isActive('/startup/dataroom')}>Data Room</Link>
            <Link to="/startup/events" className={isActive('/startup/events')}>Événements</Link>
          </>
        )}

        {user?.role === 'investisseur' && (
          <>
            <Link to="/search" className={isActive('/search')}>Explorer l'annuaire</Link>
            <Link to="/investor/watchlist" className={isActive('/investor/watchlist')}>Mes Favoris</Link>
            <Link to="/investor/portfolio" className={isActive('/investor/portfolio')}>Mon Portefeuille</Link>
            <Link to="/investor/compare" className={isActive('/investor/compare')}>Comparer</Link>
            <Link to="/investor/alerts" className={isActive('/investor/alerts')}>Alertes</Link>
            <Link to="/investor/profile" className={isActive('/investor/profile')}>Profil</Link>
          </>
        )}
        {user?.role === 'jury' && (
          <>
            {/* Correspondance exacte : avec startsWith, « Espace Jury » restait
                surligné sur toutes les sous-pages /jury/... */}
            <Link to="/jury" className={location.pathname === '/jury' ? 'active' : ''}>Espace Jury</Link>
            <Link to="/jury/dashboard" className={isActive('/jury/dashboard')}>Dashboard Trophées</Link>
            <JuryMessagesLink className={isActive('/jury/messages')} />
          </>
        )}
        {user?.role === 'analyste' && (
          <>
            <Link to="/analyst/dossiers" className={isActive('/analyst/dossiers')}>Dossiers</Link>
            <Link to="/dashboard" className={isActive('/dashboard')}>Dashboard</Link>
            <Link to="/matrix" className={isActive('/matrix')}>Matrice</Link>
          </>
        )}
        {user?.role === 'admin' && (
          <>
            <Link to="/search" className={isActive('/search')}>Annuaire</Link>
            <Link to="/admin/dashboard" className={isActive('/admin/dashboard')}>Analytics</Link>
            <Link to="/analyst/dossiers" className={isActive('/analyst/dossiers')}>Dossiers</Link>
            <Link to="/admin/campaigns" className={isActive('/admin/campaigns')}>Trophées</Link>
            <Link to="/admin/jury-members" className={isActive('/admin/jury-members')}>Jury</Link>
            <Link to="/admin/tools" className={isActive('/admin/tools')}>Outils</Link>
            <Link to="/admin/users" className={isActive('/admin/users')}>Utilisateurs</Link>
            <Link to="/admin/weights" className={isActive('/admin/weights')}>Pondérations</Link>
          </>
        )}
      </div>
      {/* Pied du menu, profil administrateur uniquement : c'est lui qui a besoin
          de savoir quelle version tourne, pour signaler une anomalie ou verifier
          qu'une livraison est bien passee. .navbar-links porte flex:1, ce bloc
          se cale donc en bas de la colonne sans positionnement absolu. */}
      {user?.role === 'admin' && (
        <div className="navbar-footer">
          <div className="navbar-footer-org">Investance Partners</div>
          <div>Plateforme ISI</div>
          <div className="navbar-footer-version">{VERSION_LABEL}</div>
        </div>
      )}
    </nav>
  );
}

// Roles for which a notification center is implemented.
// Currently only admin has notifications wired (getAdminNotifications).
const ROLES_WITH_NOTIFICATIONS = new Set(['admin']);

function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Anonymous users still see a minimal topbar with just the theme toggle
  if (!user) {
    return (
      <header className="topbar">
        <ThemeToggle />
      </header>
    );
  }
  const handleLogout = () => { logout(); navigate('/'); };
  const hasNotifications = ROLES_WITH_NOTIFICATIONS.has(user.role);
  const roleLabel = {
    startup: 'Startup', investisseur: 'Investisseur', jury: 'Jury',
    analyste: 'Analyste', admin: 'Administrateur',
  }[user.role] || user.role;

  return (
    <header className="topbar">
      {hasNotifications && <NotificationBell />}
      <ThemeToggle />
      <div className="topbar-user-info">
        <span className="topbar-user-name">{user.first_name} {user.last_name}</span>
        <span className="topbar-user-role">{roleLabel}</span>
      </div>
      <button onClick={handleLogout} className="btn btn-topbar-logout">
        Déconnexion
      </button>
    </header>
  );
}

// Resets the scroll position to the top of the page whenever the route
// changes. Without this, React-Router preserves the previous scroll
// position — so clicking a footer link (e.g. "Mentions légales") from
// the bottom of the landing would land the user mid-page on the new
// route. Mounted inside <Router> so useLocation is available.
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    // Use 'instant' (not 'smooth') — fastest perceived behavior for
    // a route transition. Falls back to a plain (0,0) call on browsers
    // that don't support the options object.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <ToastProvider>
      <Router>
        <ScrollToTop />
        <NavbarToggle />
        <Navbar />
        <TopBar />
        <div className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route path="/cgu" element={<ConditionsUtilisationPage />} />
          <Route path="/reglement" element={<ReglementPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/startup/:id" element={<StartupDetail />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/matrix" element={<MatrixPage />} />

          <Route path="/startup/dashboard" element={<ProtectedRoute roles={['startup']}><StartupDashboard /></ProtectedRoute>} />
          <Route path="/startup/profile" element={<ProtectedRoute roles={['startup']}><StartupProfile /></ProtectedRoute>} />
          <Route path="/startup/qit" element={<ProtectedRoute roles={['startup']}><QitPage /></ProtectedRoute>} />
          <Route path="/startup/dataroom" element={<ProtectedRoute roles={['startup']}><DataRoomPage /></ProtectedRoute>} />
          <Route path="/startup/events" element={<ProtectedRoute roles={['startup']}><StartupEvents /></ProtectedRoute>} />

          <Route path="/investor/portfolio" element={<ProtectedRoute roles={['investisseur']}><PortfolioPage /></ProtectedRoute>} />
          <Route path="/investor/watchlist" element={<ProtectedRoute roles={['investisseur']}><WatchlistPage /></ProtectedRoute>} />
          <Route path="/investor/compare" element={<ProtectedRoute roles={['investisseur']}><ComparePage /></ProtectedRoute>} />
          <Route path="/investor/alerts" element={<ProtectedRoute roles={['investisseur']}><InvestorAlerts /></ProtectedRoute>} />
          <Route path="/investor/profile" element={<ProtectedRoute roles={['investisseur']}><InvestorProfile /></ProtectedRoute>} />

          <Route path="/jury" element={<ProtectedRoute roles={['jury', 'admin']}><JurySpace /></ProtectedRoute>} />
          <Route path="/jury/dashboard" element={<ProtectedRoute roles={['jury', 'admin']}><JuryDashboard /></ProtectedRoute>} />
          <Route path="/jury/members" element={<ProtectedRoute roles={['jury', 'admin']}><JuryMembers /></ProtectedRoute>} />
          {/* Messagerie réservée au rôle jury : l'admin n'y a pas accès, ni en page ni via l'API. */}
          <Route path="/jury/messages" element={<ProtectedRoute roles={['jury']}><JuryMessages /></ProtectedRoute>} />

          <Route path="/analyst/dossiers" element={<ProtectedRoute roles={['analyste', 'admin']}><AnalystDossiers /></ProtectedRoute>} />
          <Route path="/analyst/dossiers/:id" element={<ProtectedRoute roles={['analyste', 'admin']}><DossierDetail /></ProtectedRoute>} />

          <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/jury-members" element={<ProtectedRoute roles={['admin']}><AdminJuryMembers /></ProtectedRoute>} />
          <Route path="/admin/weights" element={<ProtectedRoute roles={['admin']}><AdminWeights /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/campaigns" element={<ProtectedRoute roles={['admin']}><AdminCampaigns /></ProtectedRoute>} />
          <Route path="/admin/scraping" element={<ProtectedRoute roles={['admin']}><AdminScraping /></ProtectedRoute>} />
          <Route path="/admin/tools" element={<ProtectedRoute roles={['admin']}><AdminTools /></ProtectedRoute>} />
        </Routes>
        </div>
      </Router>
      </ToastProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
